import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Baby, LayoutDashboard, ShieldCheck, ArrowRight, QrCode } from "lucide-react";
import { useLang } from "../i18n";
import { BrandWordmark, BrandMascot } from "../components/Brand";
import { LanguageSwitcher } from "../components/LanguageSwitcher";

export default function Home() {
  const { t } = useLang();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen lf-grain bg-[#FFEEBC]" data-testid="home-page">
      <header className="mx-auto flex max-w-6xl items-center justify-between px-5 py-6">
        <BrandWordmark />
        <LanguageSwitcher />
      </header>

      <main className="mx-auto max-w-6xl px-5 pb-16 pt-6">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12 text-center"
        >
          <BrandMascot className="lf-float mx-auto mb-2 h-36 w-36 sm:h-44 sm:w-44" />
          <span className="inline-flex items-center gap-2 rounded-full border border-[#26422A]/50 bg-[#FFFFFF] px-4 py-1.5 text-sm font-bold text-[#26422A]">
            <ShieldCheck className="h-4 w-4 text-[#26422A]" />
            {t("safeSupervised")}
          </span>
          <h1 className="mt-6 font-display text-4xl font-black leading-tight text-[#26422A] sm:text-5xl lg:text-6xl">
            {t("playgroundCheckIn")}
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-base font-medium text-[#26422A] md:text-lg">
            {t("playgroundSubtitle")}
          </p>
        </motion.div>

        <div className="mx-auto grid max-w-4xl gap-6 sm:grid-cols-2">
          <motion.button
            data-testid="checkin-kids-card"
            onClick={() => navigate("/parent/phone")}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            whileHover={{ y: -6 }}
            className="group relative overflow-hidden rounded-3xl bg-[#26422A] p-8 text-left shadow-xl transition-shadow hover:shadow-2xl"
          >
            <div className="lf-float mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#FFEEBC]">
              <Baby className="h-9 w-9 text-[#26422A]" strokeWidth={2.2} />
            </div>
            <h2 className="font-display text-2xl font-bold text-[#FFEEBC]">
              {t("checkInKids")}
            </h2>
            <p className="mt-2 text-sm font-medium text-[#FFEEBC]/70">
              {t("tapToStart")}
            </p>
            <ArrowRight className="mt-6 h-6 w-6 text-[#FFEEBC] transition-transform group-hover:translate-x-2" />
            <div className="pointer-events-none absolute -bottom-10 -right-10 h-40 w-40 rounded-full bg-[#26422A]/40" />
          </motion.button>

          <motion.button
            data-testid="host-dashboard-card"
            onClick={() => navigate("/host")}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            whileHover={{ y: -6 }}
            className="group relative overflow-hidden rounded-3xl border-2 border-[#26422A]/50 bg-[#FFFFFF] p-8 text-left shadow-lg transition-shadow hover:shadow-xl"
          >
            <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#26422A]">
              <LayoutDashboard className="h-9 w-9 text-[#FFEEBC]" strokeWidth={2.2} />
            </div>
            <h2 className="font-display text-2xl font-bold text-[#26422A]">
              {t("hostDashboard")}
            </h2>
            <p className="mt-2 text-sm font-medium text-[#26422A]">
              {t("hostDashboardSub")}
            </p>
            <ArrowRight className="mt-6 h-6 w-6 text-[#26422A] transition-transform group-hover:translate-x-2" />
          </motion.button>
        </div>

        <div className="mt-10 text-center">
          <button
            data-testid="kiosk-qr-link"
            onClick={() => navigate("/qr")}
            className="inline-flex items-center gap-2 rounded-full border border-[#26422A]/50 bg-[#FFFFFF] px-5 py-2.5 text-sm font-bold text-[#26422A] transition-colors hover:bg-[#26422A]/10"
          >
            <QrCode className="h-4 w-4" /> {t("kioskQR")}
          </button>
        </div>
      </main>
    </div>
  );
}
